import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { PefData } from '@sportello-balneari/pef-editor';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent,
} from 'ng-apexcharts';

// ---------------------------------------------------------------------------
// Data-key constants (flattenPefApiData convention: dot-joined path).
// Scalar KPIs live under `.param`; year series use `.${year}`.
// ---------------------------------------------------------------------------
const FCF = 'flussoDiCassaPrevisionale';

const KPI_KEYS = {
  vanProgetto: `${FCF}.vanProgetto.param`,
  tirProgetto: `${FCF}.tirProgetto.param`,
  vanAzionista: `${FCF}.vanAzionista.param`,
  tirAzionista: `${FCF}.tirAzionista.param`,
  wacc: 'tassoDiScontoWacc.wacc',
  ke: 'tassoDiScontoWacc.costoDelCapitale',
} as const;

const CHART_ROW_KEYS = {
  dscr: `${FCF}.dscr`,
  llcr: `${FCF}.llcr`,
  ebitda: 'contoEconomicoPrevisionale.ebitda',
} as const;

// ---------------------------------------------------------------------------

interface KpiCard {
  label: string;
  value: string;
  positive: boolean | null;
  secondary?: { label: string; value: string };
}

function fmtCurrency(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}

function fmtPercent(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(2)} %`;
}

@Component({
  selector: 'lib-pef-summary',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChartComponent],
  template: `
    <!-- KPI Cards -->
    <div class="mb-6 grid grid-cols-2 gap-4">
      @for (kpi of kpis(); track kpi.label) {
        <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div
            class="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400"
          >
            {{ kpi.label }}
          </div>
          <div class="flex items-baseline gap-2">
            <span
              class="text-2xl font-bold"
              [class.text-slate-800]="kpi.positive === null"
              [class.text-emerald-600]="kpi.positive === true"
              [class.text-red-500]="kpi.positive === false"
            >
              {{ kpi.value }}
            </span>
            @if (kpi.secondary) {
              <span class="text-sm font-medium text-slate-400">
                · {{ kpi.secondary.label }} {{ kpi.secondary.value }}
              </span>
            }
          </div>
        </div>
      }
    </div>

    <!-- Charts -->
    <div class="mb-4 grid grid-cols-2 gap-4">
      <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h3 class="mb-1 text-sm font-semibold text-slate-600">DSCR</h3>
        <apx-chart
          [series]="dscrSeries()"
          [chart]="lineChart"
          [xaxis]="xaxis()"
          [stroke]="stroke"
          [dataLabels]="noDataLabels"
          [grid]="grid"
          [tooltip]="tooltipRatio"
          [yaxis]="yaxisDscr()"
          [colors]="['#00325a']"
        />
      </div>
      <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h3 class="mb-1 text-sm font-semibold text-slate-600">LLCR</h3>
        <apx-chart
          [series]="llcrSeries()"
          [chart]="lineChart"
          [xaxis]="xaxis()"
          [stroke]="stroke"
          [dataLabels]="noDataLabels"
          [grid]="grid"
          [tooltip]="tooltipRatio"
          [yaxis]="yaxisLlcr()"
          [colors]="['#00325a']"
        />
      </div>
    </div>
    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h3 class="mb-1 text-sm font-semibold text-slate-600">EBITDA</h3>
      <apx-chart
        [series]="debitoSeries()"
        [chart]="lineChart"
        [xaxis]="xaxis()"
        [stroke]="stroke"
        [dataLabels]="noDataLabels"
        [grid]="grid"
        [tooltip]="tooltipCurrency"
        [yaxis]="yaxisCurrency"
        [colors]="['#00325a']"
      />
    </div>
  `,
})
export class PefSummaryComponent {
  readonly data = input.required<PefData>();
  readonly startYear = input.required<number>();
  readonly endYear = input.required<number>();

  protected readonly years = computed(() => {
    const result: number[] = [];
    for (let y = this.startYear(); y <= this.endYear(); y++) result.push(y);
    return result;
  });

  protected readonly xaxis = computed<ApexXAxis>(() => ({
    categories: this.years().map(String),
    labels: { style: { fontSize: '11px' } },
  }));

  protected readonly kpis = computed<KpiCard[]>(() => {
    const d = this.data();
    const van = (key: string) => d[key] as number | undefined;
    const percent = (key: string) => {
      if (d[key] == null) return undefined;
      return (d[key] as number) / 100;
    };
    const isPositive = (v: number | null | undefined) => {
      if (v == null) return null;
      return v >= 0;
    };
    return [
      {
        label: 'VAN – Progetto',
        value: fmtCurrency(van(KPI_KEYS.vanProgetto)),
        positive: isPositive(van(KPI_KEYS.vanProgetto)),
      },
      {
        label: 'TIR – Progetto',
        value: fmtPercent(percent(KPI_KEYS.tirProgetto)),
        positive: null,
        secondary: { label: 'WACC', value: fmtPercent(percent(KPI_KEYS.wacc)) },
      },
      {
        label: 'VAN – Azionista',
        value: fmtCurrency(van(KPI_KEYS.vanAzionista)),
        positive: isPositive(van(KPI_KEYS.vanAzionista)),
      },
      {
        label: 'TIR – Azionista',
        value: fmtPercent(percent(KPI_KEYS.tirAzionista)),
        positive: null,
        secondary: { label: 'Ke', value: fmtPercent(percent(KPI_KEYS.ke)) },
      },
    ];
  });

  private seriesFor(rowKey: string, name: string) {
    return computed<ApexAxisChartSeries>(() => {
      const d = this.data();
      return [
        {
          name,
          data: this.years().map(
            (y) => (d[`${rowKey}.${y}`] as number) ?? null,
          ),
        },
      ];
    });
  }

  protected readonly dscrSeries = this.seriesFor(CHART_ROW_KEYS.dscr, 'DSCR');
  protected readonly llcrSeries = this.seriesFor(CHART_ROW_KEYS.llcr, 'LLCR');
  protected readonly debitoSeries = this.seriesFor(
    CHART_ROW_KEYS.ebitda,
    'EBITDA',
  );

  // ── Static chart config ───────────────────────────────────────────────────

  protected readonly lineChart: ApexChart = {
    type: 'line',
    height: 220,
    toolbar: { show: false },
    zoom: { enabled: false },
    fontFamily: 'inherit',
  };

  protected readonly barChart: ApexChart = {
    type: 'bar',
    height: 220,
    toolbar: { show: false },
    fontFamily: 'inherit',
  };

  protected readonly stroke: ApexStroke = { curve: 'smooth', width: 2.5 };
  protected readonly noDataLabels: ApexDataLabels = { enabled: false };
  protected readonly grid: ApexGrid = {
    borderColor: '#f1f5f9',
    strokeDashArray: 4,
  };

  protected readonly tooltipRatio: ApexTooltip = {
    y: { formatter: (v) => (v != null ? v.toFixed(2) : '—') },
  };

  protected readonly tooltipCurrency: ApexTooltip = {
    y: {
      formatter: (v) =>
        v != null
          ? v.toLocaleString('it-IT', {
              style: 'currency',
              currency: 'EUR',
              maximumFractionDigits: 0,
            })
          : '—',
    },
  };

  private ratioYaxis(series: () => ApexAxisChartSeries) {
    return computed<ApexYAxis>(() => {
      const nums = ((series()[0]?.data ?? []) as (number | null)[]).filter(
        (v): v is number => v != null,
      );
      const min = nums.length ? Math.min(...nums) : 0;
      return {
        min: min < 0 ? min * 1.1 : undefined,
        labels: { formatter: (v) => v.toFixed(2), style: { fontSize: '11px' } },
      };
    });
  }

  protected readonly yaxisDscr = this.ratioYaxis(() => this.dscrSeries());
  protected readonly yaxisLlcr = this.ratioYaxis(() => this.llcrSeries());

  protected readonly yaxisCurrency: ApexYAxis = {
    labels: {
      formatter: (v) => {
        if (v >= 1_000_000) return `€ ${(v / 1_000_000).toFixed(1)}M`;
        if (v >= 1_000) return `€ ${(v / 1_000).toFixed(0)}k`;
        return `€ ${v.toFixed(0)}k`;
      },
      style: { fontSize: '11px' },
    },
  };
}
